import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Ui } from '@silvertours/front-shared';

export const Form = styled(Ui.Card)<{
  $isThin?: boolean;
  $isModernStage?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing['40']};
  max-width: 1220px;

  ${({ $isThin, $isModernStage, theme }) =>
    $isThin
      ? `
        width: 100%;
        border: none;
        box-shadow: none;
        padding: ${theme.spacing['20']} ${theme.spacing['40']};
      `
      : `
        padding: ${theme.spacing['40']};
        position: absolute;
        top: ${theme.spacing['70']};
        left: ${theme.spacing['40']};
        right: ${theme.spacing['40']};
        margin-inline: auto;
        ${$isModernStage ? 'box-shadow: 0 4px 14px rgba(0,0,0,0.12);' : ''}
      `}
`;

export const Headline = styled.p`
  font-family: ${({ theme }) => theme.fontFamily.alternate};
  font-size: 1.125rem;
  font-weight: ${({ theme }) => theme.fontWeight.heading};
  color: ${({ theme }) => theme.colors.brand.green120};
`;

export const Icon = styled.span`
  grid-area: icon;
  display: inline-flex;
  align-items: center;
  padding-inline: ${({ theme }) =>
    `${theme.spacing['40']} ${theme.spacing['20']}`};
  border: 1px solid ${({ theme }) => theme.colors.neutral.grey120};
  border-right: 0;
  border-top-left-radius: ${({ theme }) => theme.radius.sm};
  border-bottom-left-radius: ${({ theme }) => theme.radius.sm};
  height: 100%;
  fill: ${({ theme }) => theme.colors.neutral.grey120};
`;

export const SubmitButtonWrapper = styled.span`
  display: inline-flex;
  align-items: center;

  svg {
    fill: ${({ theme }) => theme.colors.neutral.white100};
  }
`;

export const Placeholder = styled.span<{
  $isThin?: boolean;
}>`
  grid-area: placeholder;
  display: block;
  padding-right: ${({ theme }) => theme.spacing['20']};
  border: 1px solid ${({ theme }) => theme.colors.neutral.grey120};
  ${({ $isThin, theme }) =>
    $isThin
      ? `
      border-top-left-radius: ${theme.radius.sm};
      border-bottom-left-radius: ${theme.radius.sm};
      padding-inline: ${theme.spacing['40']};
    `
      : 'border-left: 0;'}
  border-top-right-radius: ${({ theme }) => theme.radius.sm};
  border-bottom-right-radius: ${({ theme }) => theme.radius.sm};
  height: 100%;
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  line-height: 3.5rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const AccessibleLabel = styled.span`
  grid-area: label;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.sm};
  height: 100%;
  color: ${({ theme }) => theme.colors.neutral.white100};
  background-color: ${({ theme }) => theme.colors.brand.green120};
  font-family: ${({ theme }) => theme.fontFamily.alternate};
  font-weight: bold;
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border-width: 0;
`;

export const SearchButton = styled.button<{
  hasValue: boolean;
  $isThin?: boolean;
}>`
  display: grid;
  grid-template: ${({ $isThin }) =>
    $isThin
      ? "'placeholder label' 3.5rem / minmax(0, 1fr) max-content"
      : "'icon placeholder' 3.5rem 'label label' 3.5rem / max-content minmax(0, 1fr)"};
  align-items: center;
  gap: ${({ $isThin, theme }) =>
    $isThin ? `0 ${theme.spacing['40']}` : `${theme.spacing['20']} 0`};
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.none};
  color: inherit;
  background: none;
  width: 100%;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm.size};
  text-align: start;
  text-transform: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: ${({ theme }) => theme.spacing['5']} dashed
      ${({ theme }) => theme.colors.brand.orange100};
    outline-offset: ${({ theme }) => theme.spacing['10']};
  }

  ${Placeholder} {
    color: ${({ theme, hasValue }) =>
      hasValue ? theme.colors.neutral.grey140 : theme.colors.neutral.grey120};
  }

  ${({ $isThin }) =>
    $isThin &&
    css`
      ${AccessibleLabel} {
        padding-inline: 1rem;
      }
    `}
`;
